import { useCallback, useEffect, useState } from 'react';

import type { FundPolicy } from '../../../core/fundPolicy/schema';
import type { FundPolicyPort } from './ports/fundPolicyPort';

/**
 * Loads the stored fund policy once per session.
 *
 * Three screens need it — dependants, declaration, documents — and it changes
 * only when the committee edits settings. Fetching it per screen would be a
 * read per tab switch for a document that has not moved (§11.5.1).
 */
export interface FundPolicyState {
  readonly policy: FundPolicy | null;
  readonly errorAr: string | null;
  /** Re-reads it, for after the committee edits it. */
  readonly reload: () => void;
}

export function useFundPolicy(port: FundPolicyPort, enabled: boolean): FundPolicyState {
  const [policy, setPolicy] = useState<FundPolicy | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    port
      .getCurrentPolicy()
      .then((loaded) => {
        if (!cancelled) setPolicy(loaded);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل إعدادات الصندوق.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [port, enabled, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return { policy, errorAr, reload };
}
